"use client";

import Image from "next/image";
import { Check } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { BlockProps } from "../BlocksManager/registry";

const variantStyles: any = {
  jaune: {
    card: "bg-yellow-light border-4 border-yellow-light hover:border-yellow",
    bubble: "bg-yellow",
  },
  orange: {
    card: "bg-orange-light border-4 border-orange-light hover:border-orange",
    bubble: "bg-orange",
  },
  vert: {
    card: "bg-green-light border-4 border-green-light hover:border-green",
    bubble: "bg-green",
  },
};

const AUTO_SLIDE_DELAY = 3500;

export default function NosProfils({ data }: BlockProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [mobileHeight, setMobileHeight] = useState<number>(0);
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);

  if (!data) return null;

  const profils = data?.profils || [];

  useEffect(() => {
    if (profils.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % profils.length);
    }, AUTO_SLIDE_DELAY);

    return () => clearInterval(interval);
  }, [profils.length]);

  useEffect(() => {
    const updateHeight = () => {
      const activeSlide = slideRefs.current[currentIndex];
      if (activeSlide) {
        setMobileHeight(activeSlide.offsetHeight);
      }
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);

    return () => window.removeEventListener("resize", updateHeight);
  }, [currentIndex, profils]);

  return (
    <section className="bg-white py-16" key={data.id}>
      <div className="container w-full">
        <h2 className="font-display mb-10 text-[32px] font-bold leading-[1.28] text-text-body md:text-[56px]">
          {data.titre}
        </h2>

        {/* Mobile / tablette : slider dynamique */}
        <div className="xl:hidden">
          <div
            className="overflow-hidden transition-[height] duration-500 ease-in-out"
            style={{ height: mobileHeight ? `${mobileHeight}px` : "auto" }}
          >
            <div
              className="flex items-start transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {profils.map((item: any, index: number) => {
                const variante = item.couleur?.toLowerCase?.().trim();
                const style = variantStyles[variante] || variantStyles.jaune;
                const iconUrl = item.icon?.url;

                return (
                  <div
                    key={item.id}
                    ref={(el) => {
                      slideRefs.current[index] = el;
                    }}
                    className="w-full shrink-0 px-1"
                  >
                    <article
                      className={`relative overflow-hidden rounded-[25px] px-6 pb-8 pt-8 shadow-sm ${style.card}`}
                    >
                      <div
                        className={`absolute left-6 top-10 h-24 w-24 rounded-full ${style.bubble}`}
                      />
                      <div
                        className={`absolute left-20 top-10 h-8 w-8 rounded-full ${style.bubble}`}
                      />
                      <div
                        className={`absolute -right-16 top-20 h-44 w-44 rounded-full ${style.bubble}`}
                      />
                      <div
                        className={`absolute bottom-20 -left-6 h-14 w-14 rounded-full ${style.bubble}`}
                      />

                      <div className="relative z-10 flex flex-col">
                        <div className="mb-10">
                          {iconUrl && (
                            <div className="relative h-[72px] w-[72px]">
                              <Image
                                src={iconUrl}
                                alt={item.icon?.alternativeText || item.label || "Icône"}
                                fill
                                className="object-contain"
                              />
                            </div>
                          )}
                        </div>

                        <h3 className="lg:text-[28px] text-[22px]  font-bold leading-[1.15] text-text-body">
                          {item.label}
                        </h3>

                        <div className="mt-6 space-y-4">
                          {item.solutions?.map((solution: any, solutionIndex: number) => (
                            <div key={solutionIndex} className="flex items-start gap-3">
                              <Check className="mt-1 h-5 w-5 shrink-0 text-black" />
                              <p className="text-[18px] leading-[1.3] text-black">
                                {solution.text}
                              </p>
                            </div>
                          ))}
                        </div>

                        {item.conclusion && (
                          <p className="mt-8 text-[18px] lg:text-[22px] font-medium leading-[1.3] text-black">
                            {item.conclusion}
                          </p>
                        )}
                      </div>
                    </article>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Dots */}
          {profils.length > 1 && (
            <div className="mt-6 flex justify-center gap-2">
              {profils.map((_: any, index: number) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => setCurrentIndex(index)}
                  className={`h-3 w-3 rounded-full transition ${
                    currentIndex === index ? "bg-pink" : "bg-blue/30"
                  }`}
                  aria-label={`Aller à la slide ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Desktop : grille normale */}
        <div className="hidden gap-6 xl:grid xl:grid-cols-3">
          {profils.map((item: any) => {
            const variante = item.couleur?.toLowerCase?.().trim();
            const style = variantStyles[variante] || variantStyles.jaune;
            const iconUrl = item.icon?.url;

            return (
              <article
                key={item.id}
                className={`relative min-h-125 overflow-hidden rounded-[25px] px-8 pb-8 pt-10 hover:shadow-xl ${style.card}`}
              >
                <div
                  className={`absolute left-10 top-12 h-27.5 w-27.5 rounded-full ${style.bubble}`}
                />
                <div
                  className={`absolute left-30 top-12 h-10 w-10 rounded-full ${style.bubble}`}
                />
                <div
                  className={`absolute -right-25 top-16 h-50 w-50 rounded-full ${style.bubble}`}
                />
                <div
                  className={`absolute bottom-28 -left-8.75 h-17.5 w-17.5 rounded-full ${style.bubble}`}
                />

                <div className="relative z-10 flex h-full flex-col">
                  <div className="mb-12">
                    {iconUrl && (
                      <div className="relative h-21.5 w-21.5">
                        <Image
                          src={iconUrl}
                          alt={item.icon?.alternativeText || item.label || "Icône"}
                          fill
                          className="object-contain"
                        />
                      </div>
                    )}
                  </div>

                  <h3 className="text-[28px] font-bold leading-[1.15] text-text-body">
                    {item.label}
                  </h3>

                  <div className="mt-6 space-y-4">
                    {item.solutions?.map((solution: any, index: number) => (
                      <div key={index} className="flex items-start gap-3">
                        <Check className="mt-1 h-5 w-5 shrink-0 text-black" />
                        <p className="text-[20px] leading-[1.22] text-black">
                          {solution.text}
                        </p>
                      </div>
                    ))}
                  </div>

                  {item.conclusion && (
                    <p className="mt-8 text-[22px] font-medium leading-[1.3] text-black">
                      {item.conclusion}
                    </p>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}